import Color, { type ColorInstance } from "color";

export interface LinkColorInput {
  backgroundColor: string; // any CSS color string
  primaryColor: string; // any CSS color string
}

export function computeLinkColor({
  backgroundColor,
  primaryColor,
}: LinkColorInput): string {
  const bg = Color(backgroundColor);
  const primary = Color(primaryColor);

  const isDarkBg = bg.isDark();

  // System "universal" link palettes – you can tweak these.
  const BASE_LINK_COLORS_LIGHT = [
    "#165dff", // blue
    "#444ce7", // indigo
    "#0d9383", // teal
    "#7c3aed", // violet
  ];

  const BASE_LINK_COLORS_DARK = [
    "#6aa8ff", // blue
    "#9aa8ff", // indigo-ish
    "#5edfd3", // teal
    "#c4b5fd", // violet
  ];

  const baseCandidates = (
    isDarkBg ? BASE_LINK_COLORS_DARK : BASE_LINK_COLORS_LIGHT
  ).map((hex) => Color(hex));

  const CONTRAST_TARGET = 4.5;
  const MAX_ADJUST_STEPS = 12;

  const primaryHue = getHueSafe(primary);
  const primaryIsGray = primary.saturationl() < 10; // arbitrary "grayish" threshold

  let bestColor: ColorInstance | null = null;
  let bestScore = -Infinity;

  for (const base of baseCandidates) {
    // 1) Ensure sufficient contrast against background
    const contrasted = adjustForContrast(
      base,
      bg,
      CONTRAST_TARGET,
      MAX_ADJUST_STEPS,
    );

    const contrast = contrasted.contrast(bg);
    if (contrast < CONTRAST_TARGET) {
      // Skip if we still can't reach contrast target
      continue;
    }

    // 2) Compute hue distance from primary (we want this to be LARGE)
    let hueScore: number;

    if (primaryIsGray) {
      // If primary is basically gray, then any saturated color is fine.
      hueScore = 180; // treat as max separation
    } else {
      const hue = getHueSafe(contrasted);
      hueScore = circularHueDistance(hue, primaryHue); // 0..180
    }

    // 3) Slightly prefer stronger contrast as tie-breaker
    const contrastBonus = (contrast - CONTRAST_TARGET) * 10;

    const score = hueScore + contrastBonus;

    if (score > bestScore) {
      bestScore = score;
      bestColor = contrasted;
    }
  }

  // Fallback: if something went wrong, pick a very visible blue.
  if (!bestColor) {
    const fallback = isDarkBg ? Color("#6aa8ff") : Color("#165dff");
    return ensureContrastFallback(fallback, bg, CONTRAST_TARGET)
      .hex()
      .toLowerCase();
  }

  return bestColor.hex().toLowerCase();
}

/**
 * Adjusts color’s lightness so that contrast with bg >= targetContrast.
 * For dark backgrounds we lighten; for light backgrounds we darken.
 */
function adjustForContrast(
  color: ColorInstance,
  bg: ColorInstance,
  targetContrast: number,
  maxSteps: number,
): ColorInstance {
  let current = color;
  const darkBg = bg.isDark();

  let steps = 0;
  while (current.contrast(bg) < targetContrast && steps < maxSteps) {
    current = darkBg ? current.lighten(0.1) : current.darken(0.1);
    steps++;
  }
  return current;
}

/**
 * “Hard” fallback adjustment if our normal approach fails.
 */
function ensureContrastFallback(
  color: ColorInstance,
  bg: ColorInstance,
  targetContrast: number,
): ColorInstance {
  let c = color;
  const darkBg = bg.isDark();
  let safety = 0;

  while (c.contrast(bg) < targetContrast && safety < 20) {
    c = darkBg ? c.lighten(0.12) : c.darken(0.12);
    safety++;
  }
  return c;
}

/**
 * Circular hue distance in degrees (0..180).
 */
function circularHueDistance(h1: number, h2: number): number {
  const diff = Math.abs(h1 - h2);
  return diff > 180 ? 360 - diff : diff;
}

/**
 * Safe hue getter: for grayscale colors, Color.hue() may be NaN.
 */
function getHueSafe(c: ColorInstance): number {
  const h = c.hue();
  if (Number.isNaN(h)) {
    // For grayscale, pretend the hue is 0 – callers can special-case grayscale if needed.
    return 0;
  }
  return h;
}
